<template>
    <div class="universal-table-container">
        <!-- 操作栏 -->
        <div class="table-operations">
            <el-button type="primary" @click="handleAdd">
                <el-icon>
                    <Plus />
                </el-icon> 新增
            </el-button>
            <!-- 分类检索的select -->
            <el-select
                v-if="enableCategorySearch"
                v-model="selectedCategory"
                :placeholder="categoryPlaceholder"
                class="category-select"
                @change="handleCategoryChange"
                clearable>
                <el-option
                    v-for="option in categoryOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value">
                </el-option>
            </el-select>
            <!-- 导出excel-->
            <el-button type="primary" v-if="enableExcelExport" @click="handleExportClick">
                <el-icon>
                    <Download />
                </el-icon> Excel导出
            </el-button>
            <el-input v-model="searchKeyword" placeholder="搜索" class="search-input" clearable
                @keyup.enter.native="handleSearch">
                <template #append>
                    <el-button icon="Search" @click="handleSearch"></el-button>
                </template>
            </el-input>
        </div>
        <!-- 数据表格 -->
        <el-table :data="tableData" border stripe style="width: 100%" @selection-change="handleSelectionChange">
            <el-table-column type="selection" width="55" v-if="enableSelection"></el-table-column>

            <!-- 动态生成列 -->
            <el-table-column v-for="column in columns" :key="column.prop" :prop="column.prop" :label="column.label"
                :width="column.width" :align="column.align || 'left'">
                <template #default="scope">
                    <template v-if="column.type === 'image'">
                        <el-image
                            :src="scope.row.coverUrl"
                            :preview-src-list="scope.row.allImageUrls"
                            style="width: 60px; height: 60px; object-fit: cover" fit="cover"
                            :preview-teleported="true"></el-image>
                    </template>
                    <!-- 逗号分割展示 -->
                    <template v-else-if="column.type === 'tag-list'">
                        <el-tag
                            v-for="(item, idx) in splitCommaStr(scope.row[column.prop])"
                            :key="idx"
                            :closable="false"
                            style="margin: 0 4px 4px 0;font-size: 16px; ">
                            {{ item }}
                        </el-tag>
                    </template>
                    <template v-else-if="column.formatter">
                        {{ column.formatter(scope.row[column.prop], scope.row, scope.$index) }}
                    </template>
                    <template v-else>
                        {{ scope.row[column.prop] }}
                    </template>
                </template>
            </el-table-column>

            <!-- 操作列 -->
            <el-table-column label="操作" width="220" align="center">
                <template #default="scope">
                    <el-button type="primary" size="small" @click="handleView(scope.row)">
                        查看
                    </el-button>
                    <el-button type="primary" size="small" @click="handleEdit(scope.row)" v-if="enableEdit">
                        编辑
                    </el-button>
                    <el-button type="danger" size="small" text-color="#ff4d4f" @click="handleDelete(scope.row)"
                        v-if="enableDelete">
                        删除
                    </el-button>
                </template>
            </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
            <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
                :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pagination.pageSize"
                layout="total, sizes, prev, pager, next, jumper" :total="pagination.total"></el-pagination>
        </div>

        <!-- 表单弹窗 (新增/编辑) -->
        <el-dialog :title="formDialog.title" v-model="formDialog.visible" :width="formDialog.width || '600px'">
            <el-form ref="dataForm" :model="formData" :rules="formRules" label-width="120px">
                <el-form-item v-for="field in formFields" :key="field.prop" :label="field.label" :prop="field.prop"
                    :required="field.required">
                    <!-- 图片上传入特殊处理 -->
                    <template v-if="field.type === 'image-upload'">
                        <el-upload
                            class="image-uploader"
                            :action="field.uploadUrl || '/api/upload'"
                            :show-file-list="true"
                            :multiple="true"
                            :file-list="formatFileList()"
                            :on-success="(response, file, fileList) => handleImageUploadSuccess(response, field.prop, fileList)"
                            :before-upload="handleBeforeImageUpload"
                            list-type="picture-card"
                            :limit="field.limit || 5"
                            :on-exceed="handleExceed"
                            @remove="(file, fileList) => handleRemove(file, fileList, field.prop)">
                            <div class="upload-placeholder">
                                <el-icon class="avatar-uploader-icon">
                                    <Plus />
                                </el-icon>
                                <div class="upload-text">点击上传图片</div>
                            </div>
                        </el-upload>
                        <div class="upload-hint" v-if="field.hint">
                            {{ field.hint }}
                        </div>
                    </template>
                    <!-- 下拉列表类型处理 -->
                    <template v-else-if="field.type === 'select'">
                        <el-select
                            v-model="formData[field.prop]"
                            :placeholder="`请选择${field.label}`"
                            v-bind="field.props || {}">
                            <!-- 渲染下拉选项（从 field.options 中获取） -->
                            <el-option
                                v-for="option in field.options"
                                :key="option.value"
                                :label="option.label"
                                :value="option.value"></el-option>
                        </el-select>
                    </template>
                    <!-- 复选框组处理 -->
                    <template v-else-if="field.type === 'checkbox-group'">
                        <el-checkbox-group
                            v-model="formData[field.prop]"
                            v-bind="field.props || {}">
                            <el-checkbox
                                v-for="option in field.options"
                                :key="option.value"
                                :label="option.value"
                                :disabled="option.disabled"
                                style="margin-right: 15px;">
                                {{ option.label }}
                            </el-checkbox>
                        </el-checkbox-group>
                    </template>
                    <!-- 逗号分割输入框 -->
                    <template v-else-if="field.type === 'tag-input'">
                        <div class="tag-input-container">
                            <!-- 已选标签展示 -->
                            <el-tag
                                v-for="(tag, idx) in getTagList(field.prop)"
                                :key="idx"
                                closable
                                @close="removeTag(idx, field.prop)"
                                style="margin: 0 4px 4px 0;font-size: 16px; ">
                                {{ tag }}
                            </el-tag>
                            <!-- 新增标签输入框 -->
                            <el-input
                                v-model="tempTagInput[field.prop]"
                                size="small"
                                placeholder="输入后按回车添加"
                                @keyup.enter="addTag(field.prop)"
                                class="tag-input"
                                style="width: 120px; margin-bottom: 4px;">
                            </el-input>
                            <!-- 新增按钮 -->
                            <el-button
                                size="small"
                                type="primary"
                                icon="Plus"
                                @click="addTag(field.prop)"
                                style="margin-bottom: 4px; margin-left: 8px;">
                                添加
                            </el-button>
                        </div>
                    </template>
                    <template v-else>
                        <component :is="getFieldComponent(field.type)" v-model="formData[field.prop]"
                            v-bind="field.props || {}" :placeholder="`请输入${field.label}`"></component>
                    </template>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="formDialog.visible = false">取消</el-button>
                <el-button type="primary" @click="handleFormSubmit">确定</el-button>
            </template>
        </el-dialog>

        <!-- 查看弹窗 -->
        <el-dialog title="查看详情" v-model="viewDialogVisible" width="600px">
            <el-descriptions column="1" border>
                <el-descriptions-item v-for="field in formFields" :key="field.prop" :label="field.label">
                    <template v-if="field.type === 'image-upload' && formData[field.prop]">
                        <el-image
                            :src="formData.coverUrl"
                            :preview-src-list="formData.allImageUrls"
                            style="max-width: 100%; max-height: 300px" fit="contain"></el-image>
                    </template>
                    <!-- 查看弹窗中格式化复选框值 -->
                    <template v-else-if="field.type === 'checkbox-group' && formData[field.prop]">
                        <!-- 将数组/逗号字符串转为易读的标签文本 -->
                        <span v-for="(val, idx) in formatCheckboxViewValue(field, formData[field.prop])" :key="idx">
                            {{ val }}
                            <span v-if="idx < formatCheckboxViewValue(field, formData[field.prop]).length - 1">，</span>
                        </span>
                    </template>
                    <!-- 逗号分割展示 -->
                    <template v-else-if="field.type === 'tag-input' && formData[field.prop]">
                        <el-tag
                            v-for="(item, idx) in splitCommaStr(formData[field.prop])"
                            :key="idx"
                            :closable="false"
                            style="margin: 0 4px 4px 0;font-size: 16px; ">
                            {{ item }}
                        </el-tag>
                    </template>
                    <template v-else>
                        {{ formData[field.prop] }}
                    </template>
                </el-descriptions-item>
            </el-descriptions>
            <template #footer>
                <el-button @click="viewDialogVisible = false">关闭</el-button>
            </template>
        </el-dialog>
        <!-- 在模板中添加导出确认弹窗 -->
        <el-dialog title="导出确认" v-model="exportDialogVisible" :width="exportDialogWidth || '400px'"
            :before-close="handleExportCancel">
            <p>{{ exportConfirmMessage }}</p>
            <template #footer>
                <el-button @click="handleExportCancel">取消</el-button>
                <el-button type="primary" @click="handleExportConfirm">确定</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import baseData from '@/api/base.js';
import { handleImgUrl } from '@/utils/MyUtils';
import { Plus, Search, Upload, Download } from '@element-plus/icons-vue';
import {
    ElInput,
    ElSelect,
    ElOption,
    ElDatePicker,
    ElCheckbox,
    ElRadioGroup,
    ElCheckboxGroup,
    ElRadio,
    ElUpload,
    ElImage
} from 'element-plus'

export default {
    name: 'UniversalDataTable',
    components: {
        Plus,
        Search,
        Upload,
        ElInput,
        ElSelect,
        ElOption,
        ElDatePicker,
        ElCheckbox,
        ElCheckboxGroup,
        ElRadioGroup,
        ElRadio,
        ElUpload,
        ElImage
    },
    props: {
        // 表格列配置
        columns: {
            type: Array,
            required: true,
            description: '表格列配置，包含prop、label、type等信息，type为image时特殊处理'
        },
        // 表单字段配置
        formFields: {
            type: Array,
            required: true,
            description: '表单字段配置，用于新增和编辑'
        },
        // 表格数据
        tableData: {
            type: Array,
            default: () => []
        },
        // 分页信息
        pagination: {
            type: Object,
            default: () => ({
                currentPage: 1,
                pageSize: 10,
                total: 0
            })
        },
        // 表单验证规则
        formRules: {
            type: Object,
            default: () => ({})
        },
        // 图片上传接口地址，默认为/api/upload
        uploadUrl: {
            type: String,
            default: '/api/upload'
        },
        // 是否启用选择功能
        enableSelection: {
            type: Boolean,
            default: true
        },
        // 是否启用编辑功能
        enableEdit: {
            type: Boolean,
            default: true
        },
        // 是否启用删除功能
        enableDelete: {
            type: Boolean,
            default: true
        },
        // 是否启用分类检索
        enableCategorySearch: {
            type: Boolean,
            default: false,
            description: '是否显示分类检索下拉框'
        },
        // 分类检索选项列表
        categoryOptions: {
            type: Array,
            default: () => [],
            description: '分类检索下拉选项，格式：[{label: "名称", value: "值"}, ...]'
        },
        //分类检索提示词
        categoryPlaceholder: {
            type: String,
            default: "请选择分类",
            description: "下拉菜单的提示词"
        },
        // 是否启用Excel导出功能
        enableExcelExport: {
            type: Boolean,
            default: false
        },
        // 导出确认消息（父组件自定义）
        exportConfirmMessage: {
            type: String,
            default: '确定要导出数据吗？'
        },
        // 导出弹窗宽度
        exportDialogWidth: {
            type: String,
            default: '400px'
        }
    },
    data() {
        return {
            // 搜索关键词
            searchKeyword: '',
            // 选中的行
            selectedRows: [],
            // 表单弹窗配置
            formDialog: {
                visible: false,
                title: '新增数据',
                isEdit: false
            },
            // 表单数据
            formData: {},
            // 查看弹窗显示状态
            viewDialogVisible: false,
            selectedCategory: '',//分类检索选中值
            tempTagInput: {},
            exportDialogVisible: false
        }
    },
    methods: {
        // 获取字段对应的组件
        getFieldComponent(type) {
            const componentMap = {
                input: 'el-input',
                select: 'el-select',
                date: 'el-date-picker',
                checkbox: 'el-checkbox',
                radio: 'el-radio-group'
            }
            return componentMap[type] || 'el-input'
        },

        // 处理搜索
        handleSearch() {
            //发送通知给父组件，通知名为sarch，传递参数this.searchKeyword
            this.$emit('search', this.searchKeyword)
        },
        //处理分类切换
        handleCategoryChange(val) {
            // 触发分类切换事件，传递选中值
            this.$emit('category-change', val);
        },
        // 处理新增
        handleAdd() {
            this.formDialog = {
                visible: true,
                title: '新增数据',
                isEdit: false
            }
            // 初始化表单数据
            this.formData = this.formFields.reduce((obj, field) => {
                if (field.type === 'checkbox-group') {
                    obj[field.prop] = field.defaultValue || [];
                } else if (field.type === 'tag-input') {
                    obj[field.prop] = field.defaultValue || ''; // 存储逗号分隔字符串
                    this.tempTagInput[field.prop] = ''; // 初始化临时输入框
                }
                else {
                    obj[field.prop] = field.defaultValue || '';
                }
                return obj
            }, {})
            // 重置表单验证
            this.$nextTick(() => {
                this.$refs.dataForm.clearValidate()
            })
        },

        // 处理编辑
        handleEdit(row) {
            this.formDialog = {
                visible: true,
                title: '编辑数据',
                isEdit: true
            }
            // 复制行数据到表单
            this.formData = { ...row }
            this.formFields.forEach(field => {
                if (field.type === 'checkbox-group' && this.formData[field.prop]) {
                    // 如果后端返回的是逗号分隔字符串，转为数组
                    if (typeof this.formData[field.prop] === 'string') {
                        this.formData[field.prop] = this.formData[field.prop].split(',').filter(val => val);
                    }
                } else if (field.type === 'tag-input') {
                    this.tempTagInput[field.prop] = ''; // 清空临时输入框
                }
            })
        },

        // 处理查看
        handleView(row) {
            this.formData = { ...row }
            this.viewDialogVisible = true
        },

        // 处理删除
        handleDelete(row) {
            this.$confirm(`确定要删除该${this.formFields[0]?.label || '数据'}吗?`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                this.$emit('delete', row)
            }).catch(() => {
                this.$message.info('已取消删除')
            })
        },

        // 处理表单提交
        handleFormSubmit() {
            this.$refs.dataForm.validate(valid => {
                if (valid) {

                    this.formFields.forEach(field => {
                        if (field.type === 'checkbox-group' && this.formData[field.prop]) {
                            // 数组转逗号分隔字符串
                            this.formData[field.prop] = Array.isArray(this.formData[field.prop])
                                ? this.formData[field.prop].join(',')
                                : this.formData[field.prop];
                        }
                        if (field.type === 'date-picker' && this.formData[field.prop]) {
                            this.formData[field.prop] += ' 00:00:00'
                        }
                    })
                    if (this.formDialog.isEdit) {
                        this.$emit('update', this.formData)
                    } else {
                        this.$emit('create', this.formData)
                    }
                    this.formDialog.visible = false
                }
            })
        },

        // 处理分页大小变化
        handleSizeChange(size) {
            this.$emit('pagination-change', { ...this.pagination, pageSize: size, currentPage: 1 })
        },

        // 处理当前页变化
        handleCurrentChange(page) {
            this.$emit('pagination-change', { ...this.pagination, currentPage: page })
        },

        // 处理选择变化
        handleSelectionChange(rows) {
            this.selectedRows = rows
            this.$emit('selection-change', rows)
        },
        //图片数据回显方法，抽取
        formatFileList() {
            return handleImgUrl(this.formData.allImageUrls)
        },
        // 图片上传成功处理
        handleImageUploadSuccess(response, prop) {
            const newImageUrl = response?.data || ''; // 后端返回的当前图片URL
            if (!newImageUrl) {
                this.$message.warning('未获取到图片地址，上传失败');
                return;
            }
            // 2. 获取表单中已有的图片字符串（首次上传为空，用空字符串兜底）
            const existingUrls = this.formData[prop] || '';
            // 3. 拼接新URL：如果已有内容，先加*分隔符，再拼接新URL；否则直接用新URL
            const updatedUrls = existingUrls ? `${existingUrls}*${newImageUrl}` : newImageUrl;
            // 4. 更新表单数据（用*分隔的字符串）
            this.formData[prop] = updatedUrls;
            // 可选：打印日志方便调试
            console.log(`当前图片列表（*分隔）：`, this.formData[prop]);
        },

        // 图片上传前验证
        handleBeforeImageUpload(file) {
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp'
            const isLt2M = file.size / 1024 / 1024 < 2

            if (!isJpgOrPng) {
                this.$message.error('只能上传JPG、PNG或WEBP格式的图片')
                return false
            }
            if (file.name.includes('*')) {
                this.$message.error('文件名不能包含 "*" 字符，请重命名后上传');
                return false; // 阻止上传
            }
            if (!isLt2M) {
                this.$message.error('图片大小不能超过2MB')
                return false
            }

            return true
        },
        // 处理文件超出限制
        handleExceed(files, fileList) {
            this.$message.warning(`最多只能上传 ${this.field.limit || 5} 张图片`);
        },
        // 处理删除图片TODO
        handleRemove(file, fileList, prop) {
            // 1. 获取当前要删除的图片URL
            //1.1 先上传再删除的file是有response的。编辑已有的图片需要通过relativePath获取路径
            const removeUrl = file.response?.data || file.relativePath;
            // 2. 将已有的字符串按*分割成数组
            const existingUrls = this.formData[prop] || '';
            const urlArray = existingUrls.split('*');
            // 3. 过滤掉要删除的URL，再用*拼接成新字符串
            const updatedArray = urlArray.filter(url => url !== removeUrl);
            if (this.formData.allImageUrls) {
                this.formData.allImageUrls = this.formData.allImageUrls.filter(url => url !== file.url);
            }
            this.formData[prop] = updatedArray.join('*');
        },
        // 格式化查看弹窗中的复选框值（转为标签文本）
        formatCheckboxViewValue(field, value) {
            if (!value) return [];
            // 统一转为数组
            const valueArr = typeof value === 'string' ? value.split(',').filter(val => val) : value;
            // 匹配选项的label文本
            return valueArr.map(val => {
                const option = field.options.find(item => item.value === val);
                return option ? option.label : val;
            });
        },
        //逗号分割字符串
        // 1. 分割逗号字符串为数组（通用）
        splitCommaStr(str) {
            if (!str || typeof str !== 'string') return [];
            return str.split(',').map(item => item.trim()).filter(item => item);
        },
        // 2. 获取标签列表（从表单数据的逗号字符串转换）
        getTagList(prop) {
            return this.splitCommaStr(this.formData[prop] || '');
        },
        // 3. 添加标签
        addTag(prop) {
            const tag = this.tempTagInput[prop]?.trim();
            if (!tag) {
                this.$message.warning('请输入有效内容');
                return;
            }
            const tagList = this.getTagList(prop);
            if (tagList.includes(tag)) {
                this.$message.warning('该标签已存在');
                return;
            }
            // 拼接新标签（逗号分隔）
            this.formData[prop] = tagList.concat([tag]).join(',');
            // 清空临时输入框
            this.tempTagInput[prop] = '';
        },
        // 4. 删除标签
        removeTag(index, prop) {
            const tagList = this.getTagList(prop);
            tagList.splice(index, 1);
            // 重新拼接为逗号分隔字符串
            this.formData[prop] = tagList.join(',');
        },
        // 处理导出按钮点击
        handleExportClick() {
            this.exportDialogVisible = true;
        },
        // 处理导出确认
        handleExportConfirm() {
            this.exportDialogVisible = false;
            // 触发导出事件，由父组件处理具体请求
            this.$emit('export-excel');
        },
        // 处理导出取消
        handleExportCancel() {
            this.exportDialogVisible = false;
        }
    }
}
</script>

<style scoped>
.universal-table-container {
    padding: 20px;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    height: 100%;
}

.table-operations {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.category-select {
    width: 200px;
    margin-right: 16px;
}

.search-input {
    width: 300px;
}

.pagination-container {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
}

/* 图片上传样式 */
.avatar-uploader {
    display: inline-block;
}

.upload-placeholder {
    width: 150px;
    height: 150px;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: border-color 0.3s;
}

.upload-placeholder:hover {
    border-color: #409eff;
}

.avatar-uploader-icon {
    font-size: 28px;
    color: #8c8c8c;
}

.upload-text {
    margin-top: 8px;
    font-size: 14px;
    color: #8c8c8c;
}

.upload-hint {
    margin-top: 8px;
    font-size: 12px;
    color: #8c8c8c;
}



.universal-table-container :deep(.el-table__cell) {
    padding: 15px 0;
    /* 上下内边距 15px（默认约 8px），左右保持默认（可加 left/right 控制） */
}

/* 适配不同屏幕尺寸 */
@media (max-width: 768px) {
    .table-operations {
        flex-direction: column;
        align-items: flex-start;
    }

    .search-input {
        width: 100%;
        margin-top: 10px;
    }

    .category-select {
        width: 100%;
        margin-right: 0;
        margin-bottom: 10px;
    }

    .pagination-container {
        justify-content: center;
    }
}

.tag-input-container {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;
}

.tag-input {
    flex-shrink: 0;
}
</style>
